import { api } from "./api";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const STORAGE_KEY = "mp_utm";
const SESSION_KEY = "mp_session_id";
const USER_ID_KEY = "mp_user_id";

// ---------------------------------------------------------------------------
// Resilient localStorage wrapper.
// In strict Incognito modes (Chrome with 3P cookies blocked, iOS Safari
// private, some enterprise browsers) `localStorage.setItem` throws
// `SecurityError` or `QuotaExceededError`. We fall back to an in-memory
// Map so downstream code never sees a thrown exception — critical because
// several call sites (like MobileCaptureDialog.submit) have blanket
// try/catches that would otherwise mis-report the failure as "Could not
// save. Try again." even when the API call itself succeeded.
// ---------------------------------------------------------------------------
const memStore = new Map();
function lsGet(key) {
  try {
    return localStorage.getItem(key);
  } catch (_) {
    return memStore.has(key) ? memStore.get(key) : null;
  }
}
function lsSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (_) {
    memStore.set(key, value);
  }
}

function uuid() {
  return "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function getSessionId() {
  let sid = lsGet(SESSION_KEY);
  if (!sid) {
    sid = uuid();
    lsSet(SESSION_KEY, sid);
  }
  return sid;
}

export function getUserId() {
  // Stable anonymous user id — 5-6 digit numeric, persisted per browser.
  // Backend also generates one server-side; we let backend be authoritative
  // by only storing what backend returns via trackClick response.
  return lsGet(USER_ID_KEY) || "";
}

export function setUserId(id) {
  if (id) lsSet(USER_ID_KEY, String(id));
}

export function getStoredUTM() {
  try {
    return JSON.parse(lsGet(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function parseFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const utm = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) utm[k] = v;
  });
  return utm;
}

export async function initTracking() {
  const sessionId = getSessionId();
  const fromUrl = parseFromUrl();
  const stored = getStoredUTM();
  // Attribution: first-touch wins. Only overwrite if nothing stored yet.
  const merged = Object.keys(stored).length ? stored : fromUrl;
  if (Object.keys(fromUrl).length && !Object.keys(stored).length) {
    lsSet(STORAGE_KEY, JSON.stringify(fromUrl));
  }
  try {
    await api.post("/track/session", {
      session_id: sessionId,
      ...merged,
      referrer: document.referrer || "",
      landing_url: window.location.href,
      user_agent: navigator.userAgent,
    });
  } catch (_) {
    // silent
  }
  return { sessionId, utm: merged };
}

export function withUtm(url) {
  if (!url) return url;
  const utm = getStoredUTM();
  if (!Object.keys(utm).length) return url;
  try {
    const u = new URL(url);
    Object.entries(utm).forEach(([k, v]) => u.searchParams.set(k, v));
    return u.toString();
  } catch {
    return url;
  }
}

export async function trackClick(product_id) {
  const utm = getStoredUTM();
  try {
    const { data } = await api.post("/track/click", {
      product_id,
      session_id: getSessionId(),
      user_id: getUserId() || undefined,
      ...utm,
      referrer: document.referrer || "",
      current_url: window.location.href,
    });
    if (data?.user_id) setUserId(data.user_id);
    return data;
  } catch (_) {
    return null;
  }
}
